/**
 * SmartLeadMagnet — exit-intent popup that captures email for a free
 * "2026 Landlord Tax Checklist" PDF. Triggers when the visitor's cursor
 * leaves the viewport heading toward the address bar (desktop) or after
 * 35s of dwell time (mobile fallback). One-shot per visitor (localStorage).
 */
import { useEffect, useState } from "react";
import { X, Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { trackWithPersonalization } from "@/lib/analytics";
import { usePersonalization } from "@/lib/personalizer";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const SHOWN_KEY = "cr_leadmagnet_shown";

export default function SmartLeadMagnet() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const personal = usePersonalization();

  useEffect(() => {
    if (localStorage.getItem(SHOWN_KEY) === "1") return;
    let triggered = false;
    function fire() {
      if (triggered) return;
      triggered = true;
      localStorage.setItem(SHOWN_KEY, "1");
      setOpen(true);
    }
    function onMouseOut(e) {
      if (e.clientY <= 5 && !e.toElement && !e.relatedTarget) fire();
    }
    const mobileTimer = setTimeout(fire, 35000);
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      clearTimeout(mobileTimer);
    };
  }, []);

  async function submit() {
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/smart-chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: `leadmagnet_${Date.now()}`,
          message: "(exit-intent lead capture — visitor requested tax checklist)",
          lead_email: email.trim(),
          page_path: window.location.pathname,
        }),
      });
      trackWithPersonalization("lead_capture", personal, {
        page_path: window.location.pathname,
        cta_label: "lead-magnet",
        meta: { email: email.trim(), source: "exit-intent" },
      });
      setDone(true);
    } catch { /* no-op */ }
    finally { setBusy(false); }
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-5" data-testid="lead-magnet" onClick={() => setOpen(false)}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setOpen(false)} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700"><X className="w-4 h-4"/></button>
        {!done ? (
          <>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200">
              <Sparkles className="w-3.5 h-3.5 text-emerald-700"/>
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Free download</span>
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-slate-900 leading-tight">
              Wait — grab the <span className="text-emerald-700">2026 Canadian Landlord Tax Checklist</span> before you go.
            </h2>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Every T776 deduction, CRA filing deadline, and provincial nuance in one printable PDF. Used by 2,400+ Canadian landlords.
            </p>
            <label htmlFor="lead-magnet-email-input" className="mt-5 block text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              <span>Your email</span>
              <span className="text-rose-600 font-bold ml-0.5">*</span>
            </label>
            <input
              id="lead-magnet-email-input"
              type="email"
              required
              aria-required="true"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="you@example.com"
              data-testid="lead-magnet-email"
              autoFocus
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
            <button
              onClick={submit}
              disabled={!email.trim() || busy}
              data-testid="lead-magnet-submit"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm rounded-lg disabled:opacity-60"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Sending…</> : "Email me the checklist"}
            </button>
            <p className="mt-2 text-[11px] text-slate-400 text-center">No spam. Unsubscribe anytime.</p>
          </>
        ) : (
          <div className="text-center py-4" data-testid="lead-magnet-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto"/>
            <h2 className="mt-3 font-display text-xl font-bold text-slate-900">Check your inbox.</h2>
            <p className="mt-2 text-sm text-slate-600">The 2026 Canadian Landlord Tax Checklist is on its way.</p>
            <button onClick={() => setOpen(false)} className="mt-5 text-sm text-emerald-700 font-semibold hover:underline">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
